import axios from "axios";
import CommonDropzone from "components/common/CommonDropzone";
import CommonTextField from "components/common/CommonTextField";
import React, { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";

import { generateURLOptions } from "URLOptions";
import {
  GET_EXERCISES_CATEGORIES,
  UPLOAD_EXERCISE_IMAGE,
  UPLOAD_EXERCISE_VIDEO,
} from "URLRoutes";

const AddExercise = ({ isOpen, onClose, onSubmit }) => {
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(null);
  const [videoURL, setVideoURL] = useState("");
  const [imageURL, setImageURL] = useState("");

  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getExerciseCategories();
    } else {
      setTitle("");
      setCategory(null);
      setVideoURL("");
      setImageURL("");
    }
  }, [isOpen]);

  const getExerciseCategories = async () => {
    try {
      const response = await axios.get(GET_EXERCISES_CATEGORIES, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setCategories(response.data?.data);
    } catch (error) {
      console.error("Get Exercise Categories Error: ", error);
      alert(error);
    }
  };

  const uploadVideo = async (video) => {
    const formData = new FormData();
    formData.append("video", video);
    try {
      setUploadingVideo(true);
      const response = await axios.post(UPLOAD_EXERCISE_VIDEO, formData, {
        headers: generateURLOptions("multipart/form-data"),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setVideoURL(response.data?.data);
    } catch (error) {
      console.error("Upload Exercise Video Error: ", error);
      alert(error);
    } finally {
      setUploadingVideo(false);
    }
  };

  const uploadImage = async (image) => {
    const formData = new FormData();
    formData.append("image", image);
    try {
      setUploadingImage(true);
      const response = await axios.post(UPLOAD_EXERCISE_IMAGE, formData, {
        headers: generateURLOptions("multipart/form-data"),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setImageURL(response.data?.data);
    } catch (error) {
      console.error("Upload Exercise Image Error: ", error);
      alert(error);
    } finally {
      setUploadingImage(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-lg font-semibold">Create Exercise</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <IoCloseOutline size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4">
          <CommonTextField
            label="Exercise Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Exercise Title"
          />

          {/* Category */}
          <div>
            <p className="mb-1 font-semibold text-sm text-gray-800">
              Exercise Category
            </p>
            <select
              onChange={(e) => setCategory(e.target.value)}
              value={category ?? ""}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select a category</option>
              {categories?.map((category) => (
                <option key={category?._id} value={category?._id}>
                  {category?.name ?? ""}
                </option>
              ))}
            </select>
          </div>

          {/* Image Upload */}
          <div>
            <p className="mb-1 font-semibold text-sm text-gray-800">Image</p>
            <div className="flex gap-3 items-start mb-3">
              {imageURL && (
                <img
                  src={imageURL}
                  alt="Exercise Upload"
                  className="w-2/5 h-auto object-cover rounded-lg"
                />
              )}
              <CommonDropzone
                title="Image"
                value={imageURL}
                dropFile={uploadImage}
                isUploading={uploadingImage}
              />
            </div>
          </div>

          {/* Video Upload */}
          <div>
            <p className="mb-1 font-semibold text-sm text-gray-800">Video</p>
            <div className="flex flex-row justify-center items-center">
              {videoURL && (
                <video
                  src={videoURL}
                  controls
                  className="w-full h-48 mb-3 mr-3"
                />
              )}
              <CommonDropzone
                value={videoURL}
                dropFile={uploadVideo}
                isUploading={uploadingVideo}
              />
            </div>
          </div>

          <p className="text-xs text-center text-gray-600">
            The Play Duration is automatically calculated based on the video
            uploaded.
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-between mt-6">
          <button
            onClick={onClose}
            className="w-1/2 mr-2 bg-gray-200 text-gray-800 py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onSubmit({
                title,
                categoryId: category,
                video: videoURL,
                image: imageURL,
              })
            }
            type="submit"
            className="w-1/2 ml-2 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700"
          >
            Create Exercise
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddExercise;
